import { client as axios } from "./axiosClient";
import { Constants } from "@socialcoach/services";
import { catchServiceException } from "@socialcoach/services";
import * as DTO from "../interfaces/dtos";
import { ClientNotes } from "@/interfaces";
import { VideoProject } from "../interfaces/dtos";

const VIDEO_PROJECT_SERVICE = `${Constants.API_URL}/video-catalyst`;

export class VideoProjectServices {
  @catchServiceException()
  static async create(videoProject: DTO.VideoProjectRequest): Promise<DTO.VideoProject> {
    const { data } = await axios.post<DTO.VideoProject>(
      `${VIDEO_PROJECT_SERVICE}`,
      {
        ...videoProject,
      },
      {
        withCredentials: true,
      }
    );

    return data;
  }

  @catchServiceException()
  static async update(videoProject: DTO.VideoProjectUpdateRequest, id: number): Promise<DTO.VideoProject> {
    const response = await axios.put<DTO.VideoProject>(
      `${VIDEO_PROJECT_SERVICE}/${id}`,
      {
        ...videoProject,
      },
      {
        withCredentials: true,
      }
    );
    return response.data;
  }

  @catchServiceException()
  static async setAssignee(videoProjectId: number, assigneeUserId: number | null): Promise<void> {
    await axios.put<DTO.VideoProject>(
      `${VIDEO_PROJECT_SERVICE}/${videoProjectId}/assignee`,
      { assigneeUserId },
      { withCredentials: true }
    );
  }

  @catchServiceException()
  static async get(id: string | number): Promise<DTO.VideoProjectDetail> {
    const { data } = await axios.get<DTO.VideoProjectDetail>(`${VIDEO_PROJECT_SERVICE}/${id}`, {
      withCredentials: true,
    });
    return data;
  }

  @catchServiceException()
  static async getPlayersWithVideoProjects(coachId: string | number): Promise<DTO.PlayerResumeResponse[]> {
    const { data } = await axios.get<DTO.PlayerResumeResponse[]>(`${VIDEO_PROJECT_SERVICE}/coach/${coachId}/players`, {
      withCredentials: true,
    });
    return data;
  }

  @catchServiceException()
  static async getPlayerVideoProjects(playerId: number): Promise<DTO.Page<DTO.VideoProject>> {
    const body = {
      playerId,
    };
    const { data } = await axios.post<DTO.Page<DTO.VideoProject>>(
      `${VIDEO_PROJECT_SERVICE}/player/search?page=0&size=400&sort=createdDate,DESC`,
      { ...body },
      {
        withCredentials: true,
      }
    );
    return data;
  }

  @catchServiceException()
  static async getLatestVideoProjectPreferences(playerId: number): Promise<DTO.VideoPreferences> {
    const { data } = await axios.get<DTO.VideoPreferences>(
      `${VIDEO_PROJECT_SERVICE}/player/preferences?playerId=${playerId}`,
      {
        withCredentials: true,
      }
    );
    return data;
  }

  @catchServiceException()
  static async getVideoCatalystStatus(playerId: number | string): Promise<DTO.VideoCatalystStatus> {
    const { data } = await axios.get<DTO.VideoCatalystStatus>(`${VIDEO_PROJECT_SERVICE}/player/${playerId}/status`, {
      withCredentials: true,
    });
    return data;
  }

  @catchServiceException()
  static async getPlayerSummaries(coachId: string | number): Promise<DTO.VideoProjectPlayerSummary[]> {
    const { data } = await axios.get<DTO.VideoProjectPlayerSummary[]>(
      `${VIDEO_PROJECT_SERVICE}/coach/${coachId}/players/summary`,
      {
        withCredentials: true,
      }
    );
    return data;
  }

  @catchServiceException()
  static async search(
    coachId: string | number,
    sort: string,
    filters?: DTO.SearchFilter | null,
    page: number | string = 0,
    size: number = 15
  ): Promise<DTO.Page<DTO.PlayerVideoProjectResponse>> {
    const customFilters = {
      coachId,
      status: filters?.status ? filters?.status : undefined,
    };

    const response = await axios.post<DTO.Page<DTO.PlayerVideoProjectResponse>>(
      `${VIDEO_PROJECT_SERVICE}/search?page=${page}&size=${size}&sort=${sort}`,
      {
        ...customFilters,
      },
      {
        withCredentials: true,
      }
    );
    return response.data;
  }

  @catchServiceException()
  static async getAll(
    coachId: string | number,
    playerId: string | number,
    sort: string = "createdDate,DESC",
    page: number | string = 0,
    size: number = 2000
  ): Promise<DTO.Page<DTO.PlayerVideoProjectResponse>> {
    const customFilters = {
      playerId,
      coachId,
    };
    const response = await axios.post<DTO.Page<DTO.PlayerVideoProjectResponse>>(
      `${VIDEO_PROJECT_SERVICE}/search?page=${page}&size=${size}&sort=${sort}`,
      {
        ...customFilters,
      },
      {
        withCredentials: true,
      }
    );
    return response.data;
  }

  @catchServiceException()
  static async getAsEditor(
    sort: string,
    filters?: DTO.SearchVideoProjectFilter | null,
    page: number | string = 0,
    size: number = 15
  ): Promise<DTO.Page<DTO.PlayerVideoProjectResponse>> {
    const response = await axios.post<DTO.Page<DTO.PlayerVideoProjectResponse>>(
      `${VIDEO_PROJECT_SERVICE}/editor/search?page=${page}&size=${size}&sort=${sort}`,
      {
        ...filters,
      },
      {
        withCredentials: true,
      }
    );
    return response.data;
  }

  @catchServiceException()
  static async videoProjectByPlayer(
    coachId?: string | number,
    playerId?: number | string,
    page: number | string = 0,
    size: number = 5,
    sort: string = "createdDate,DESC"
  ): Promise<DTO.Page<DTO.PlayerVideoProjectResponse>> {
    const customFilters = {
      playerId,
      coachId,
    };
    const response = await axios.post<DTO.Page<DTO.PlayerVideoProjectResponse>>(
      `${VIDEO_PROJECT_SERVICE}/search?page=${page}&size=${size}&sort=${sort}`,
      {
        ...customFilters,
      },
      {
        withCredentials: true,
      }
    );
    return response.data;
  }

  @catchServiceException()
  static async getCoachConfig(coachId: string | number): Promise<DTO.VideoProjectCoachConfig> {
    const { data } = await axios.get<DTO.VideoProjectCoachConfig>(`${VIDEO_PROJECT_SERVICE}/coach/${coachId}/config`, {
      withCredentials: true,
    });
    return data;
  }

  @catchServiceException()
  static async updatePlayerConfig(
    playerId: number,
    config: DTO.VideoProjectPlayerConfigRequest
  ): Promise<DTO.VideoCatalystStatus> {
    const { data } = await axios.post<DTO.VideoCatalystStatus>(
      `${VIDEO_PROJECT_SERVICE}/player/${playerId}/config`,
      config,
      { withCredentials: true }
    );
    return data;
  }

  @catchServiceException()
  static async updateCoachConfig(
    coachId: string,
    config: DTO.VideoProjectCoachConfig
  ): Promise<DTO.VideoProjectCoachConfig> {
    const { data } = await axios.post<DTO.VideoProjectCoachConfig>(
      `${VIDEO_PROJECT_SERVICE}/coach/${coachId}/config`,
      config,
      { withCredentials: true }
    );
    return data;
  }

  @catchServiceException()
  static async archive(id: number): Promise<void> {
    const { data } = await axios.put<void>(`${VIDEO_PROJECT_SERVICE}/archive/${id}`, {
      withCredentials: true,
    });
    return data;
  }

  @catchServiceException()
  static async duplicate(id: number, shouldArchiveCurrentProject: boolean): Promise<DTO.VideoProject> {
    const { data } = await axios.post<DTO.VideoProject>(
      `${VIDEO_PROJECT_SERVICE}/${id}/duplicate`,
      { shouldArchiveCurrentProject },
      {
        withCredentials: true,
      }
    );

    return data;
  }

  @catchServiceException()
  static async sendMessage(id: number, message: string): Promise<void> {
    const { data } = await axios.post<void>(
      `${VIDEO_PROJECT_SERVICE}/${id}/message`,
      {
        content: message,
      },
      {
        withCredentials: true,
      }
    );
    return data;
  }

  @catchServiceException()
  static async requestRevision(id: number, message: string): Promise<VideoProject> {
    const request = {
      content: message,
    };
    const { data } = await axios.post<VideoProject>(
      `${VIDEO_PROJECT_SERVICE}/${id}/revision`,
      {
        message: request,
      },
      {
        withCredentials: true,
      }
    );
    return data;
  }

  @catchServiceException()
  static async acknowledgeMessage(id: number, type: string = "MESSAGES"): Promise<void> {
    const { data } = await axios.put<void>(`${VIDEO_PROJECT_SERVICE}/${id}/acknowledge/${type}`, {
      withCredentials: true,
    });
    return data;
  }

  @catchServiceException()
  static async addClientNotes(playerId: number | string, notes: string): Promise<ClientNotes> {
    const { data } = await axios.post<ClientNotes>(
      `${VIDEO_PROJECT_SERVICE}/player/client-notes`,
      {
        notes,
        playerId,
      },
      {
        withCredentials: true,
      }
    );
    return data;
  }

  @catchServiceException()
  static async getPlayerNotes(playerId: number | string): Promise<ClientNotes | undefined> {
    const { data } = await axios.get<ClientNotes | undefined>(
      `${VIDEO_PROJECT_SERVICE}/player/client-notes?playerId=${playerId}`,
      {
        withCredentials: true,
      }
    );
    return data;
  }

  @catchServiceException()
  static async getCoachNotes(coachId: number | string): Promise<ClientNotes | undefined> {
    const { data } = await axios.get<ClientNotes | undefined>(
      `${VIDEO_PROJECT_SERVICE}/coach/client-notes?coachId=${coachId}`,
      {
        withCredentials: true,
      }
    );
    return data;
  }

  @catchServiceException()
  static async addCoachClientNotes(coachId: string | number, notes: string): Promise<ClientNotes> {
    const { data } = await axios.post<ClientNotes>(
      `${VIDEO_PROJECT_SERVICE}/player/client-notes`,
      {
        notes,
        coachId,
      },
      {
        withCredentials: true,
      }
    );
    return data;
  }
}
